// src/pages/WalletPage.js
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getWalletBalance, getTransactions } from '../services/walletService';
import { Wallet, Loader2, RefreshCw, CreditCard, Landmark, Bitcoin, Globe } from 'lucide-react';
import ChargeRequestModal from '../components/wallet/ChargeRequestModal';
import { convertToPersianNumbers } from '../utils/PersianNumberUtils';
import { useAuthContext } from '../context/AuthContext';
import apiClient from '../api/apiClient';   // ← اضافه شد

const WalletPage = () => {
  const { openAuthModal, getAuthTokens } = useAuthContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [balance, setBalance] = useState('0.00');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);   // مودال درخواست شارژ (کارت به کارت و ...)
  const [showChargeModal, setShowChargeModal] = useState(false);   // مودال پرداخت آنلاین
  const [showPaymentResult, setShowPaymentResult] = useState(false);
  const [paymentResultSuccess, setPaymentResultSuccess] = useState(false);
  const [paymentResultMessage, setPaymentResultMessage] = useState('');

  const [giftCode, setGiftCode] = useState('');
  const [redeemingGift, setRedeemingGift] = useState(false);
  const [giftMessage, setGiftMessage] = useState('');

  // پردازش نتیجهٔ بازگشت از درگاه
  useEffect(() => {
    const success = searchParams.get('payment_success');
    const message = searchParams.get('message');
    if (success !== null) {
      setPaymentResultSuccess(success === 'true');
      setPaymentResultMessage(message || '');
      setShowPaymentResult(true);
      // حذف پارامترها از URL
      searchParams.delete('payment_success');
      searchParams.delete('message');
      setSearchParams(searchParams, { replace: true });
      // به‌روزرسانی موجودی
      fetchData();
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [balanceRes, txRes] = await Promise.all([getWalletBalance(), getTransactions()]);
      setBalance(balanceRes.data.balance);
      setTransactions(txRes.data.results || txRes.data || []);
    } catch (err) {
      console.error('Error loading wallet data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleOpenModal = () => setIsModalOpen(true);

  // باز کردن مودال پرداخت آنلاین
  const handleOnlinePayment = () => {
    if (!getAuthTokens()) {
      openAuthModal();
      return;
    }
    setShowChargeModal(true);
  };

  // ارسال درخواست شروع پرداخت و هدایت به درگاه
  const submitOnlinePayment = async (amount) => {
    try {
      const response = await apiClient.post('/wallet/payment/initiate/', { amount });
      window.location.href = response.data.payment_url;
    } catch (err) {
      throw err;
    }
  };

  const handleRedeemGiftCode = async () => {
    if (!giftCode.trim()) {
      setGiftMessage('❌ لطفاً کد کارت هدیه را وارد کنید.');
      return;
    }
    setRedeemingGift(true);
    setGiftMessage('');

    try {
      const token = getAuthTokens();
      if (!token) {
        setGiftMessage('❌ شما وارد نشده‌اید. لطفاً وارد شوید.');
        setTimeout(() => openAuthModal(), 1500);
        setRedeemingGift(false);
        return;
      }

      const res = await fetch('https://api.farzaneh.app/api/v1/wallet/gift-cards/redeem/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ code: giftCode.trim() }),
      });
      const data = await res.json();

      if (res.ok) {
        setGiftMessage('✅ کارت هدیه با موفقیت فعال شد.');
        setTimeout(() => {
          fetchData();
          setGiftCode('');
          setGiftMessage('');
        }, 1500);
      } else if (res.status === 401) {
        setGiftMessage('❌ نشست شما منقضی شده است. لطفاً دوباره وارد شوید.');
        setTimeout(() => openAuthModal(), 1500);
      } else {
        let errorMsg = data.error || data.detail || data.message || 
                       (data.non_field_errors && data.non_field_errors[0]) || 
                       'خطا در فعال‌سازی کارت هدیه';
        const alreadyUsedKeywords = [
          'قبلاً استفاده شده', 'already used', 'استفاده شده', 'قبلا استفاده',
          'used before', 'already redeemed'
        ];
        const isAlreadyUsed = alreadyUsedKeywords.some(keyword => 
          errorMsg.toLowerCase().includes(keyword.toLowerCase())
        );
        if (isAlreadyUsed) {
          setGiftMessage('❌ قبلاً از این کد استفاده شده است.');
        } else {
          setGiftMessage(`❌ ${errorMsg}`);
        }
      }
    } catch (err) {
      console.error(err);
      setGiftMessage('❌ خطا در ارتباط با سرور');
    } finally {
      setRedeemingGift(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-10">
        <Loader2 className="animate-spin text-emerald-700" size={32} />
      </div>
    );
  }

  const formattedBalance = convertToPersianNumbers(Number(balance).toLocaleString());
  const newColor = 'oklch(38% 0.189 293.745)';

  return (
    <div className="p-4 max-w-lg mx-auto rtl">
      {/* هدر */}
      <div className="flex items-center gap-2 mb-6">
        <Wallet size={20} className="text-gray-700" />
        <h1 className="text-sm font-extrabold text-gray-700">کیف پول</h1>
      </div>

      {/* کادر موجودی */}
      <div className="border border-violet-700 rounded-xl p-4 mb-6 bg-white">
        <p className="text-violet-700 text-sm font-normal">موجودی فعلی</p>
        <p className="text-violet-700 text-sm font-bold mt-1">
          {formattedBalance} تومان
        </p>
      </div>

      {/* چهار دکمه روش‌های پرداخت */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <button
          onClick={handleOnlinePayment}
          className="flex flex-col items-center gap-2 bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition"
        >
          <CreditCard size={28} className="text-violet-800" />
          <span className="text-[11px] font-light text-gray-700">پرداخت آنلاین</span>
        </button>
        <button
          onClick={handleOpenModal}
          className="flex flex-col items-center gap-2 bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition"
        >
          <Landmark size={28} className="text-violet-700" />
          <span className="text-[11px] font-light text-gray-700">کارت به کارت</span>
        </button>
        <button
          onClick={handleOpenModal}
          className="flex flex-col items-center gap-2 bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition"
        >
          <Bitcoin size={28} className="text-violet-700" />
          <span className="text-[11px] font-light text-gray-700">رمز ارز</span>
        </button>
        <button
          onClick={handleOpenModal}
          className="flex flex-col items-center gap-2 bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition"
        >
          <Globe size={28} className="text-violet-700" />
          <span className="text-[11px] font-light text-gray-700">سایر روش‌ها</span>
        </button>
      </div>

      {/* بخش فعال‌سازی کارت هدیه */}
      <div className="mb-8 bg-gray-50 rounded-xl p-4">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">فعال‌سازی کارت هدیه</h3>
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="کد کارت هدیه را وارد کنید"
            className="flex-1 border border-gray-300 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2"
            style={{ '--tw-ring-color': newColor }}
            value={giftCode}
            onChange={(e) => setGiftCode(e.target.value)}
          />
          <button
            onClick={handleRedeemGiftCode}
            disabled={redeemingGift}
            className="text-white px-4 py-2 rounded-xl text-sm font-medium hover:opacity-90 disabled:opacity-50 transition"
            style={{ backgroundColor: newColor }}
          >
            {redeemingGift ? 'در حال فعال‌سازی...' : 'فعال‌سازی'}
          </button>
        </div>
        {giftMessage && (
          <p className={`text-xs mt-2 ${giftMessage.includes('✅') ? 'text-green-600' : 'text-red-500'}`}>
            {giftMessage}
          </p>
        )}
      </div>

      {/* تراکنش‌های اخیر */}
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-xs font-semibold text-gray-700">تراکنش‌های اخیر</h2>
        <button onClick={fetchData} className="text-emerald-700 hover:text-emerald-900">
          <RefreshCw size={16} />
        </button>
      </div>

      {transactions.length === 0 ? (
        <p className="text-gray-500 text-sm">هنوز تراکنشی ثبت نشده است.</p>
      ) : (
        <div className="space-y-0">
          {transactions.map((tx) => (
            <div key={tx.id} className="flex justify-between items-center py-3 border-b border-gray-200 last:border-b-0">
              <div>
                <p className="text-[11px] font-light text-gray-700">{tx.description || tx.type_display}</p>
                <p className="text-[10px] text-gray-400 mt-1">
                  {new Date(tx.created_at).toLocaleDateString('fa-IR')}
                </p>
              </div>
              <span className={`text-sm font-medium ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {tx.amount > 0 ? '+' : ''}
                {convertToPersianNumbers(Number(tx.amount).toLocaleString())}
              </span>
            </div>
          ))}
        </div>
      )}

      <ChargeRequestModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      {/* مودال پرداخت آنلاین */}
      {showChargeModal && (
        <ChargeModal
          onClose={() => setShowChargeModal(false)}
          onSubmit={submitOnlinePayment}
        />
      )}

      {/* مودال نتیجهٔ پرداخت */}
      {showPaymentResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 w-11/12 max-w-sm shadow-xl text-center">
            <div className="text-4xl mb-3">{paymentResultSuccess ? '✅' : '❌'}</div>
            <h2 className="text-lg font-bold mb-2">
              {paymentResultSuccess ? 'پرداخت موفق' : 'پرداخت ناموفق'}
            </h2>
            <p className="text-sm text-gray-600 mb-6">{paymentResultMessage}</p>
            <button
              onClick={() => setShowPaymentResult(false)}
              className="w-full py-2 rounded-xl bg-violet-600 text-white hover:bg-violet-700 transition"
            >
              متوجه شدم
            </button>
          </div>
        </div>
      )}

      <div className="flex justify-center mt-12 mb-6">
        <a 
          referrerPolicy='origin' 
          target='_blank' 
          href='https://trustseal.enamad.ir/?id=743356&Code=cegf5V01ZJJPrIgoeIrjGinSGGNKk3tQ'
        >
          <img 
            referrerPolicy='origin' 
            src='https://trustseal.enamad.ir/logo.aspx?id=743356&Code=cegf5V01ZJJPrIgoeIrjGinSGGNKk3tQ' 
            alt='نماد اعتماد الکترونیکی' 
            style={{ cursor: 'pointer', width: '100px', height: 'auto' }} 
          />
        </a>
      </div>
    </div>
  );
};

// ══════════ کامپوننت مودال پرداخت آنلاین (با اعداد فارسی و پسوند تومان) ══════════
const ChargeModal = ({ onClose, onSubmit }) => {
  const [amount, setAmount] = useState('');   // عدد خام انگلیسی (مثلاً '5000000')
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // مبالغ پیشنهادی (تومان)
  const presets = [
    { value: 1000000, label: '۱,۰۰۰,۰۰۰ تومان' },
    { value: 2000000, label: '۲,۰۰۰,۰۰۰ تومان' },
    { value: 5000000, label: '۵,۰۰۰,۰۰۰ تومان' },
  ];

  const handlePresetClick = (value) => {
    setAmount(String(value));
    setError('');
  };

  // تبدیل رشته ورودی (شامل ارقام فارسی/انگلیسی) به عدد خام انگلیسی
  const handleAmountChange = (e) => {
    let raw = e.target.value;
    // تبدیل ارقام فارسی به انگلیسی
    const persianDigits = '۰۱۲۳۴۵۶۷۸۹';
    const englishDigits = '0123456789';
    let converted = '';
    for (let ch of raw) {
      const idx = persianDigits.indexOf(ch);
      if (idx >= 0) {
        converted += englishDigits[idx];
      } else {
        converted += ch;
      }
    }
    // حذف همهٔ نویسه‌های غیرعددی (فقط عدد خالص)
    const digitsOnly = converted.replace(/\D/g, '');
    setAmount(digitsOnly);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const numAmount = parseInt(amount, 10);
    if (!numAmount || numAmount < 1000) {
      setError('حداقل مبلغ ۱۰۰۰ تومان است');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onSubmit(numAmount);
      // هدایت به درگاه
    } catch (err) {
      setError(err.response?.data?.error || 'خطا در برقراری ارتباط');
      setLoading(false);
    }
  };

  // نمایش مبلغ به فارسی (با ویرگول جداکننده هزارگان) یا خالی
  const displayAmount = amount ? convertToPersianNumbers(Number(amount).toLocaleString()) : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl p-6 w-11/12 max-w-sm shadow-xl">
        <h3 className="text-lg font-bold mb-4 text-center">شارژ کیف پول</h3>
        <form onSubmit={handleSubmit}>
          {/* فیلد مبلغ با پسوند تومان */}
          <div className="relative mb-3">
            <input
              type="text"
              inputMode="numeric"
              value={displayAmount}
              onChange={handleAmountChange}
              placeholder="مبلغ به تومان"
              className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm pl-14"  // pl برای جای خالی پسوند
              required
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 pointer-events-none">
              تومان
            </span>
          </div>

          {error && <p className="text-red-500 text-xs mb-2">{error}</p>}
          
          {/* دکمه‌های مبالغ پیشنهادی */}
          <div className="flex flex-wrap gap-2 mb-4">
            {presets.map((preset) => (
              <button
                key={preset.value}
                type="button"
                onClick={() => handlePresetClick(preset.value)}
                className={`px-3 py-1.5 rounded-xl text-xs font-medium border transition ${
                  amount === String(preset.value)
                    ? 'bg-violet-600 text-white border-violet-600'
                    : 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 rounded-xl bg-gray-200 text-gray-700 hover:bg-gray-300 transition"
            >
              انصراف
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2 rounded-xl bg-violet-600 text-white hover:bg-violet-700 transition disabled:opacity-50"
            >
              {loading ? '...' : 'پرداخت'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WalletPage;